import { useId } from 'react';

/**
 * repliq brand mark — "Repliq Charte Graphique" §02 (logo) and §05 (app icon).
 *
 * Brand colours are hardcoded rather than tokenized on purpose: the charte
 * forbids recolouring the logo ("✕ Ne pas recolorer"), so the mark must not
 * follow the UI theme the way the rest of the interface does.
 *
 * The one thing callers must supply is `surface`: the dot's stroke is a
 * knockout painted in the colour of whatever sits behind the logo, and that
 * stroke is what opens the gap between the dot and the bubble. Pass the actual
 * background — a hex, or `var(--token)`.
 */

/** The speech bubble, copied verbatim from the charte (viewBox 0 0 64 64). */
const BUBBLE_PATH =
  'M6 21C6 13.8 11.8 8 19 8H34C41.2 8 47 13.8 47 21V29C47 36.2 41.2 42 34 42H24L13 51V40.4C8.8 38.3 6 34.1 6 29.4Z';

/** Coral: the reply dot. A signal colour — never a background (charte §03). */
const CORAL = '#FF6252';

export type BrandVariant =
  /** Primary: iris gradient bubble. For light surfaces. */
  | 'gradient'
  /** White bubble, for plum and other dark surfaces. */
  | 'on-dark';

// The charte also defines a one-colour ink variant for print; it has no screen
// call site, so it is not implemented here.

export function BrandGlyph({
  size = 24,
  variant = 'gradient',
  surface,
  className = '',
}: {
  size?: number;
  variant?: BrandVariant;
  surface: string;
  className?: string;
}) {
  // Colons from useId() would land inside url(#…); strip them.
  const gradientId = `repliq-bubble-${useId().replace(/:/g, '')}`;
  const bubbleFill = variant === 'gradient' ? `url(#${gradientId})` : '#FFFFFF';

  return (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      className={className}
      aria-hidden
      focusable="false"
    >
      {variant === 'gradient' ? (
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#5B45F0" />
            <stop offset="1" stopColor="#9A3FF0" />
          </linearGradient>
        </defs>
      ) : null}
      <path d={BUBBLE_PATH} fill={bubbleFill} />
      {/* style, not the stroke attribute: presentation attributes don't resolve var(). */}
      <circle cx="47" cy="42" r="12" fill={CORAL} strokeWidth="4" style={{ stroke: surface }} />
    </svg>
  );
}

/**
 * The wordmark. Always lowercase — the charte forbids REPLIQ, Replik and
 * RepliQ (§01), so never place this inside an uppercasing container.
 */
export function BrandWordmark({ className = '' }: { className?: string }) {
  return <span className={`font-display tracking-[-0.03em] ${className}`}>repliq</span>;
}
